import { useState, createContext, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  ClickAwayListener,
  Icon,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import { Translator } from "@itarget/shared";

const DropdownContext = createContext();

const useStyles = makeStyles((theme) => ({
  container: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    backgroundColor: ({ theme }) => (theme === "light" ? "#fff" : "#333"),
    borderRadius: "8px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
    padding: theme.spacing(1),
    minWidth: "180px",
    zIndex: 1000,
  },
  itemLink: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    color: ({ theme }) => (theme === "light" ? "#444" : "#f2f2f2"),
    textDecoration: "none",
    fontSize: "0.85rem",
    "&:hover": {
      backgroundColor: ({ theme }) =>
        theme === "light" ? "#f9f9f9" : "#3a3a3a",
      borderRadius: "4px",
    },
  },
  icon: {
    marginRight: theme.spacing(0.75),
    fontSize: "0.95rem",
    "& i, & svg": {
      fill: ({ theme }) => (theme === "light" ? "#444" : "#f2f2f2"),
    },
  },
}));

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {String} [props.className]
 * @param {'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'} [props.position]
 */
export function Dropdown({ className, children, position = "bottom-left" }) {
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  return (
    <DropdownContext.Provider
      value={{ open, handleToggle, handleClose, position }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Box position="relative" display="inline-block" className={className} >
          {children}
        </Box>
      </ClickAwayListener>
    </DropdownContext.Provider>
  );
}

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 */
Dropdown.Button = function DropdownButton({ children, className }) {
  const { handleToggle, open } = useContext(DropdownContext);
  return (
    <Button variant="outlined" className={className} onClick={handleToggle}>
      {children}
      <Icon>{open ? "arrow_drop_up" : "arrow_drop_down"}</Icon>
    </Button>
  );
};

/**
 * @typedef {Object} DropdownItem
 * @property {string} url
 * @property {string} icon
 * @property {string} description
 */

/**
 * @param {Object} props
 * @param {DropdownItem[]} props.items
 */
Dropdown.Menu = function DropdownMenu({ items }) {
  const { open, handleClose, position } = useContext(DropdownContext);
  const systemTheme = useSelector((state) => state.theme.systemTheme);
  const classes = useStyles({ theme: systemTheme });

  const positionStyles = {
    "bottom-left": { top: "100%", left: 0 },
    "bottom-right": { top: "100%", right: 0 },
    "top-left": { bottom: "100%", left: 0 },
    "top-right": { bottom: "100%", right: 0 },
  };

  if (!open || items.length === 0) return null;

  return (
    <Box className={classes.container} style={positionStyles[position]}>
      {items.map((item, index) => (
        <a
          key={index}
          href={item.url}
          className={classes.itemLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
        >
          <Box
            component="span"
            className={classes.icon}
            dangerouslySetInnerHTML={{ __html: item.icon }}
          />
          <Typography variant="body2">
            <Translator transRef={`translation.${item.description}`} />
          </Typography>
        </a>
      ))}
    </Box>
  );
};
